import { useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";

const EMPTY_KPI = { label: "", value: "", suffix: "", trend: "", trend_label: "", note: "" };
const EMPTY_CATEGORY = { category: "", adr: "", occupancy: "", note: "" };
const EMPTY_TREND = { year: "", value: 0, label: "" };
const EMPTY_DRIVER = { label: "", value: "", source: "" };
const EMPTY_SOURCE = { name: "", url: "", year: "" };

export default function AdminMarket() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/market-data").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-white/40">Caricamento…</div>;

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setListItem = (key, idx, item) =>
    setData((d) => ({
      ...d,
      [key]: d[key].map((it, i) => (i === idx ? item : it)),
    }));
  const addToList = (key, empty) =>
    setData((d) => ({ ...d, [key]: [...d[key], { ...empty }] }));
  const removeFromList = (key, idx) =>
    setData((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/market-data", data);
      toast.success("Dati di mercato aggiornati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-market-page" className="space-y-10">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Mercato Milano</h1>
          <p className="mt-2 text-sm text-white/50 max-w-2xl">
            Dati visualizzati nella pagina pubblica <code className="text-white/70">/mercato-milano</code>.
            Aggiorna ogni anno con gli ultimi report (HVS, JLL, CBRE, STR/CoStar, Cushman & Wakefield).
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="market-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#C48F56] text-black px-6 py-3 text-sm font-medium hover:bg-[#E5B782] disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Salvataggio…" : "Salva tutto"}
        </button>
      </header>

      {/* === Hero text === */}
      <Card title="Testi della pagina">
        <Field label="Anno di riferimento">
          <input
            type="number"
            value={data.reference_year}
            onChange={(e) => setField("reference_year", parseInt(e.target.value, 10) || 2024)}
            className={inputCls}
          />
        </Field>
        <Field label="Eyebrow">
          <input value={data.eyebrow} onChange={(e) => setField("eyebrow", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Headline (usa *parola* per il corsivo)">
          <input value={data.headline} onChange={(e) => setField("headline", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Sottotitolo">
          <textarea
            value={data.subhead}
            onChange={(e) => setField("subhead", e.target.value)}
            rows={3}
            className={`${inputCls} resize-none`}
          />
        </Field>
      </Card>

      {/* === KPIs === */}
      <Card
        title="KPI principali"
        subtitle="Massimo 4 KPI consigliato — la griglia è 4 colonne su desktop."
        action={
          <ListAddBtn onClick={() => addToList("kpis", EMPTY_KPI)} label="Aggiungi KPI" />
        }
      >
        <div className="space-y-3">
          {data.kpis.map((k, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
              <input placeholder="Label" value={k.label} onChange={(e) => setListItem("kpis", i, { ...k, label: e.target.value })} className={`${inputCls} md:col-span-3`} />
              <input placeholder="Value (es. 292)" value={k.value} onChange={(e) => setListItem("kpis", i, { ...k, value: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Suffix (es. €, %)" value={k.suffix} onChange={(e) => setListItem("kpis", i, { ...k, suffix: e.target.value })} className={`${inputCls} md:col-span-1`} />
              <input placeholder="Trend (+5%)" value={k.trend} onChange={(e) => setListItem("kpis", i, { ...k, trend: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Trend label" value={k.trend_label} onChange={(e) => setListItem("kpis", i, { ...k, trend_label: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Nota (fonte)" value={k.note} onChange={(e) => setListItem("kpis", i, { ...k, note: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <RemoveBtn onClick={() => removeFromList("kpis", i)} />
            </div>
          ))}
        </div>
      </Card>

      {/* === Categories === */}
      <Card
        title="ADR per categoria"
        action={<ListAddBtn onClick={() => addToList("categories", EMPTY_CATEGORY)} label="Aggiungi categoria" />}
      >
        <div className="space-y-3">
          {data.categories.map((c, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
              <input placeholder="Categoria (es. 4 stelle)" value={c.category} onChange={(e) => setListItem("categories", i, { ...c, category: e.target.value })} className={`${inputCls} md:col-span-3`} />
              <input placeholder="ADR (€ 220–320)" value={c.adr} onChange={(e) => setListItem("categories", i, { ...c, adr: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Occupancy (≈ 75%)" value={c.occupancy} onChange={(e) => setListItem("categories", i, { ...c, occupancy: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Nota" value={c.note} onChange={(e) => setListItem("categories", i, { ...c, note: e.target.value })} className={`${inputCls} md:col-span-4`} />
              <RemoveBtn onClick={() => removeFromList("categories", i)} />
            </div>
          ))}
        </div>
      </Card>

      {/* === Trend RevPAR === */}
      <Card
        title="Trend RevPAR (indice)"
        subtitle="Valori usati come barre verticali del grafico — il più alto si colora in ottone."
        action={<ListAddBtn onClick={() => addToList("trend_revpar", EMPTY_TREND)} label="Aggiungi anno" />}
      >
        <div className="space-y-3">
          {data.trend_revpar.map((p, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
              <input placeholder="Anno" value={p.year} onChange={(e) => setListItem("trend_revpar", i, { ...p, year: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <input type="number" placeholder="Value" value={p.value} onChange={(e) => setListItem("trend_revpar", i, { ...p, value: parseInt(e.target.value, 10) || 0 })} className={`${inputCls} md:col-span-2`} />
              <input placeholder="Label sotto barra (Pre-COVID, Recovery…)" value={p.label} onChange={(e) => setListItem("trend_revpar", i, { ...p, label: e.target.value })} className={`${inputCls} md:col-span-7`} />
              <RemoveBtn onClick={() => removeFromList("trend_revpar", i)} />
            </div>
          ))}
        </div>
      </Card>

      {/* === Pipeline === */}
      <Card title="Pipeline (cittadina vs Invest Hospitality)">
        <Field label="Camere in pipeline a Milano">
          <input type="number" value={data.pipeline_citywide_rooms} onChange={(e) => setField("pipeline_citywide_rooms", parseInt(e.target.value, 10) || 0)} className={inputCls} />
        </Field>
        <Field label="Descrizione mercato">
          <input value={data.pipeline_citywide_label} onChange={(e) => setField("pipeline_citywide_label", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Camere Invest Hospitality">
          <input type="number" value={data.pipeline_our_rooms} onChange={(e) => setField("pipeline_our_rooms", parseInt(e.target.value, 10) || 0)} className={inputCls} />
        </Field>
        <Field label="Descrizione Invest Hospitality">
          <input value={data.pipeline_our_label} onChange={(e) => setField("pipeline_our_label", e.target.value)} className={inputCls} />
        </Field>
      </Card>

      {/* === Drivers === */}
      <Card
        title="Driver di domanda"
        action={<ListAddBtn onClick={() => addToList("drivers", EMPTY_DRIVER)} label="Aggiungi driver" />}
      >
        <div className="space-y-3">
          {data.drivers.map((d, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
              <input placeholder="Label (Visitatori, Passeggeri, Pipeline…)" value={d.label} onChange={(e) => setListItem("drivers", i, { ...d, label: e.target.value })} className={`${inputCls} md:col-span-4`} />
              <input placeholder="Value (9 mln, 28,9 mln…)" value={d.value} onChange={(e) => setListItem("drivers", i, { ...d, value: e.target.value })} className={`${inputCls} md:col-span-3`} />
              <input placeholder="Fonte" value={d.source} onChange={(e) => setListItem("drivers", i, { ...d, source: e.target.value })} className={`${inputCls} md:col-span-4`} />
              <RemoveBtn onClick={() => removeFromList("drivers", i)} />
            </div>
          ))}
        </div>
      </Card>

      {/* === Sources === */}
      <Card
        title="Fonti consultate"
        subtitle="Mostrate in fondo alla pagina pubblica per credibility & trasparenza."
        action={<ListAddBtn onClick={() => addToList("sources", EMPTY_SOURCE)} label="Aggiungi fonte" />}
      >
        <div className="space-y-3">
          {data.sources.map((s, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
              <input placeholder="Nome fonte" value={s.name} onChange={(e) => setListItem("sources", i, { ...s, name: e.target.value })} className={`${inputCls} md:col-span-5`} />
              <input placeholder="URL" value={s.url} onChange={(e) => setListItem("sources", i, { ...s, url: e.target.value })} className={`${inputCls} md:col-span-4`} />
              <input placeholder="Anno" value={s.year} onChange={(e) => setListItem("sources", i, { ...s, year: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <RemoveBtn onClick={() => removeFromList("sources", i)} />
            </div>
          ))}
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-10 px-10 py-4 bg-[#08080A]/90 backdrop-blur border-t border-white/10 flex items-center justify-between">
        <div className="text-xs text-white/40">
          Ultimo aggiornamento: {data.last_updated ? new Date(data.last_updated).toLocaleDateString("it-IT") : "—"}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#C48F56] text-black px-6 py-3 text-sm font-medium hover:bg-[#E5B782] disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Salvataggio…" : "Salva tutto"}
        </button>
      </div>
    </div>
  );
}

const inputCls =
  "rounded-lg border border-white/10 bg-[#08080A] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#C48F56]/40 focus:outline-none w-full";

function Card({ title, subtitle, action, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-[#0A0A0D] p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-2xl tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-white/50 mt-1 max-w-xl">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-white/45 mb-2">{label}</span>
      {children}
    </label>
  );
}

function ListAddBtn({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs hover:bg-white/5 shrink-0"
    >
      <Plus size={12} /> {label}
    </button>
  );
}

function RemoveBtn({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="md:col-span-12 md:flex md:justify-end mt-1 text-white/40 hover:text-red-400 inline-flex items-center gap-1 text-xs"
    >
      <Trash2 size={12} /> Rimuovi
    </button>
  );
}
